// Game modes described by data rules: official modes and community-created modes
// go through the same engine (Race only reads ModeRules, never the mode id).
import { ITEM_IDS, type ItemId } from './items.js';

/**
 * A mode's rules without the official items a player unticked. The list says what is off, not what is on: a saved choice
 * still holds on a mode with its own pool, and items added to the game later come in ticked. With nothing left the
 * boxes hold the mode's created items alone, or the race has no items. The same object when nothing changes.
 */
export function withoutItems<T extends { items: boolean; itemPool?: ItemId[]; builtinItems?: boolean; customItems?: unknown[] }>(rules: T, off: readonly string[] = []): T {
  if (!rules.items || rules.builtinItems === false) return rules;
  const had = rules.itemPool?.length ? rules.itemPool : ITEM_IDS, pool = had.filter(k => !off.includes(k));
  if (pool.length === had.length) return rules;
  if (pool.length) return { ...rules, itemPool: pool };
  return rules.customItems?.length ? { ...rules, builtinItems: false } : { ...rules, items: false };
}

/** Created item (contracts CustomItem): building blocks run by the engine like official items. */
export interface CustomItemDef {
  id: string; name: string; help?: string;
  content: {
    icon: string; target: 'self' | 'ahead' | 'leader' | 'allAhead' | 'all' | 'behind'; delivery: 'instant' | 'trap' | 'missile'; duration: number;
    effect: { power?: number; drag?: number; speed?: number; shield?: number; draft?: number; jam?: boolean; spin?: boolean; grade?: number; wind?: number };
    blockable: boolean; odds: { front: number; back: number };
  };
}

export type ModeId = 'race' | 'tt' | 'ttt' | 'elim' | 'ghost' | 'free' | 'workout';
export type DifficultyId = 'facile' | 'normal' | 'difficile' | 'pro';

/** End condition: laps, last survivor, timer, or open session (free ride, workout). */
export type FinishRule = 'laps' | 'survivor' | 'timer' | 'open';
/** Ranking order: position on the road, or number of hits. */
export type ScoringRule = 'position' | 'hits';
/**
 * Teams. Riders are grouped by club (riders without a club and the bots fill balanced teams of `size`) or balanced
 * automatically. A team's result is the time of its nth rider (team time trial), its best rider, or the sum of its
 * riders' ranks. Teams start together or in waves `waveGap` seconds apart, one team at a time; drafting may be
 * limited to teammates.
 */
export interface TeamRules {
  by: 'club' | 'auto';
  size: number;
  scoring: 'nth' | 'best' | 'ranks';
  nth?: number;
  start: 'together' | 'waves';
  waveGap?: number;
  draftTeamOnly?: boolean;
}

export interface ModeRules {
  finish: FinishRule;
  /** Default laps when finish = 'laps' (the player can change it). */
  laps?: number;
  /** Duration in seconds: required for 'timer', optional cap otherwise. */
  timeLimit?: number;
  items: boolean;
  /** Allowed items (all by default). */
  itemPool?: ItemId[];
  /** Draw multipliers per item (1 = normal, 0 = never). */
  itemBoost?: Partial<Record<ItemId, number>>;
  /** Arena play: boxes closer together and offensive draws. */
  arena?: boolean;
  /** The last rider is eliminated each time the leader completes a lap. */
  eliminateLast?: boolean;
  bots: boolean;
  /** 'group': bots ride together at the pace the player chooses. */
  pace?: 'race' | 'group';
  /** Drafting behind other riders (true by default). */
  draft?: boolean;
  /** Bots wait or catch up to stay with the humans (true by default). */
  rubberBand?: boolean;
  /** Race against your best recorded run. */
  ghost?: boolean;
  online: boolean;
  scoring?: ScoringRule;
  /** Created items, drawn from the boxes alongside the official ones (or alone if builtinItems is false). */
  customItems?: CustomItemDef[];
  builtinItems?: boolean;
  /** Team play (team time trial, club against club); absent: everyone for themselves. */
  teams?: TeamRules;
}

export interface ModeDef { name: string; icon: string; desc: string; hidden?: boolean; rules: ModeRules }

export const MODES: Record<ModeId, ModeDef> = {
  race: { name: 'Grand Prix', icon: '🏆', desc: 'Classic race with items. First across the line wins.', rules: { finish: 'laps', laps: 2, items: true, bots: true, online: true } },
  tt: { name: 'Time trial', icon: '⏱️', desc: 'Everyone for themselves against the bots: no items, no drafting.', rules: { finish: 'laps', laps: 2, items: false, draft: false, bots: true, online: true } },
  ttt: {
    name: 'Team time trial', icon: '👥', desc: 'Teams of four start a minute apart; no items, drafting behind teammates only. The team clock stops on its second rider.',
    rules: { finish: 'laps', laps: 2, items: false, draft: true, bots: true, online: true, teams: { by: 'club', size: 4, scoring: 'nth', nth: 2, start: 'waves', waveGap: 60, draftTeamOnly: true } },
  },
  elim: { name: 'Elimination', icon: '❌', desc: 'Each time the leader completes a lap, the last rider is eliminated.', rules: { finish: 'survivor', items: true, eliminateLast: true, bots: true, online: true } },
  ghost: { name: 'Ghost time trial', icon: '👻', desc: 'Time trial without items against your best run.', rules: { finish: 'laps', laps: 2, items: false, ghost: true, bots: false, online: false } },
  free: { name: 'Free ride', icon: '🚴', desc: 'Ride in a group at the pace you choose, no items and no finish line.', rules: { finish: 'open', items: false, bots: true, pace: 'group', online: true } },
  workout: { name: 'Workout', icon: '📈', desc: 'Structured training, ERG mode.', hidden: true, rules: { finish: 'open', items: false, bots: false, online: true } },
};

/**
 * Whether a mode's rules fit an open road (ADR 0029): one run to a finish line. Modes without one (a free ride, a
 * workout, a timed game) or counting laps (an elimination) need a loop.
 */
export const fitsOpenRoad = (r: Pick<ModeRules, 'finish' | 'eliminateLast'>): boolean => r.finish === 'laps' && !r.eliminateLast;

export const isModeId = (id: string): id is ModeId => Object.prototype.hasOwnProperty.call(MODES, id);

/** Full rules: defaults applied, safety bounds (a creation cannot throw the engine off). */
/** Team rules with every field set and bounded. */
export type ResolvedTeamRules = Required<TeamRules>;
export function resolveRules(r: Partial<ModeRules> | undefined, base: ModeRules = MODES.race.rules): Required<Omit<ModeRules, 'itemPool' | 'itemBoost' | 'teams'>> & Pick<ModeRules, 'itemPool' | 'itemBoost'> & { teams: ResolvedTeamRules | null } {
  const clampN = (v: number | undefined, lo: number, hi: number) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, v!)) : undefined);
  const m = { ...base, ...(r || {}) };
  const int = (v: number | undefined, lo: number, hi: number, d: number) => (Number.isFinite(v) ? Math.round(Math.min(hi, Math.max(lo, v!))) : d);
  const T = m.teams;
  const size = int(T?.size, 2, 8, 4);
  // a team mode needs a finish line and a plain road: no elimination, no ghost
  const teams: ResolvedTeamRules | null = T && m.finish === 'laps' && !m.eliminateLast && !m.ghost
    ? { by: T.by === 'club' ? 'club' : 'auto', size, scoring: T.scoring === 'best' || T.scoring === 'ranks' ? T.scoring : 'nth', nth: int(T.nth, 1, size, Math.min(size, 2)), start: T.start === 'waves' ? 'waves' : 'together', waveGap: int(T.waveGap, 10, 600, 60), draftTeamOnly: !!T.draftTeamOnly }
    : null;
  return {
    teams,
    finish: m.finish, laps: int(m.laps, 1, 20, 2), timeLimit: int(m.timeLimit, 0, 3 * 3600, m.finish === 'timer' ? 240 : 0),
    items: !!m.items, itemPool: m.itemPool?.length ? m.itemPool : undefined, itemBoost: m.itemBoost, arena: !!m.arena,
    eliminateLast: !!m.eliminateLast, bots: !!m.bots, pace: m.pace === 'group' ? 'group' : 'race',
    draft: m.draft !== false, rubberBand: m.rubberBand !== false, ghost: !!m.ghost, online: !!m.online && !m.ghost,
    scoring: m.scoring || 'position',
    builtinItems: m.builtinItems !== false || !m.customItems?.length,
    // a created item cannot throw the engine off: every building block is bounded here too
    customItems: (m.customItems || []).slice(0, 12).map(c => ({ ...c, content: { ...c.content, duration: clampN(c.content.duration, 0.5, 15) ?? 3, odds: { front: clampN(c.content.odds?.front, 0, 40) ?? 10, back: clampN(c.content.odds?.back, 0, 40) ?? 10 },
      effect: { ...c.content.effect, power: clampN(c.content.effect.power, 0.3, 2.5), drag: clampN(c.content.effect.drag, 0.4, 3), speed: clampN(c.content.effect.speed, 0.2, 1), shield: clampN(c.content.effect.shield, 0, 15), draft: clampN(c.content.effect.draft, 0, 0.4), grade: clampN(c.content.effect.grade, -8, 8), wind: clampN(c.content.effect.wind, -8, 15) } } })),
  };
}
export type ResolvedRules = ReturnType<typeof resolveRules>;

/** Human-readable rule labels (previews, library cards). */
export function describeRules(input: Partial<ModeRules>): string[] {
  const r = resolveRules(input), out: string[] = [];
  out.push({ laps: `First after ${r.laps} lap${r.laps > 1 ? 's' : ''}`, survivor: 'Last rider standing wins', timer: `${Math.round(r.timeLimit / 60)}-minute game`, open: 'Open game, no finish line' }[r.finish]);
  if (r.timeLimit && r.finish !== 'timer') out.push(`Time limit: ${Math.round(r.timeLimit / 60)} min`);
  if (r.eliminateLast) out.push('The last rider is eliminated each time the leader completes a lap');
  out.push(r.items ? (r.itemPool ? `Items: ${r.itemPool.length} allowed` : 'Items on') : 'No items');
  if (r.arena) out.push('Arena: boxes closer together');
  if (!r.draft) out.push('Drafting off');
  if (r.bots) out.push(r.pace === 'group' ? 'Bots ride as a group at your pace' : r.rubberBand ? 'Bots matched to your W/kg' : 'Bots never wait');
  if (r.scoring === 'hits') out.push('Ranked by number of hits');
  if (r.customItems.length) out.push(`${r.builtinItems ? 'Created items added' : 'Created items only'} : ${r.customItems.map(c => `${c.content.icon} ${c.name}`).join(', ')}`);
  if (r.ghost) out.push('Against your ghost');
  if (r.teams) {
    const T = r.teams;
    out.push(T.by === 'club' ? `Teams of ${T.size} by club` : `Balanced teams of ${T.size}`);
    out.push(T.scoring === 'nth' ? `Team time on its ${T.nth === 1 ? 'first' : T.nth === 2 ? 'second' : T.nth === 3 ? 'third' : `${T.nth}th`} rider` : T.scoring === 'best' ? 'Team ranked by its best rider' : 'Team ranked by the sum of its riders’ places');
    if (T.start === 'waves') out.push(`Teams start ${T.waveGap >= 60 && T.waveGap % 60 === 0 ? `${T.waveGap / 60} min` : `${T.waveGap} s`} apart`);
    if (T.draftTeamOnly) out.push('Drafting behind teammates only');
  }
  return out;
}

export const DIFFICULTY: Record<DifficultyId, { name: string; factor: number; level?: number }> = {
  facile: { name: 'Easy', factor: 0.82 },
  normal: { name: 'Normal', factor: 0.96 },
  difficile: { name: 'Hard', factor: 1.08 },
  pro: { name: 'Pro', factor: 1.2, level: 8 },
};
